import { useEffect, useState } from 'react';
import { Moon, Sun } from 'lucide-react';
import { cn } from '../utils/cn.js';

type Theme = 'ruled' | 'blackboard';
const STORAGE_KEY = 'kollez-theme';

function readInitialTheme(): Theme {
  if (typeof window === 'undefined') return 'ruled';
  const q = new URLSearchParams(window.location.search).get('theme');
  if (q === 'ruled' || q === 'blackboard') return q;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === 'ruled' || stored === 'blackboard') return stored;
  return 'ruled';
}

/**
 * Theme toggle: writes `data-theme="ruled|blackboard"` to `<html>` + `localStorage`.
 * Matches `shell.js`'s theme mechanism verbatim so old bookmarks with `?theme=` keep working.
 */
export function ThemeToggle({ className }: { className?: string }) {
  const [theme, setTheme] = useState<Theme>('ruled');

  useEffect(() => {
    const t = readInitialTheme();
    setTheme(t);
    document.documentElement.setAttribute('data-theme', t);
  }, []);

  const flip = () => {
    const next: Theme = theme === 'ruled' ? 'blackboard' : 'ruled';
    setTheme(next);
    document.documentElement.setAttribute('data-theme', next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // ignore quota/private-browsing errors
    }
  };

  return (
    <button
      type="button"
      className={cn('theme-toggle', className)}
      onClick={flip}
      aria-label={`Switch to ${theme === 'ruled' ? 'blackboard' : 'ruled'} theme`}
    >
      {theme === 'ruled' ? <Moon size={14} aria-hidden /> : <Sun size={14} aria-hidden />}
      <span>{theme === 'ruled' ? 'Blackboard' : 'Ruled'}</span>
    </button>
  );
}
